/**
 * Environment detection utilities
 *
 * This utility provides consistent environment detection across the application.
 * It handles both client-side and server-side rendering scenarios.
 */

/**
 * Checks if the current environment is production
 * @returns boolean indicating if running in production
 */
export const isProduction = (): boolean => {
  if (typeof window === 'undefined') {
    // Server-side rendering - check API URL from environment variable
    const apiUrl = process.env.NEXT_PUBLIC_BEATLY_API_URL || '';
    return apiUrl.includes('api-v2.beatly.com') && !apiUrl.includes('staging');
  }
  return window.location.hostname === 'app.beatly.com';
};

/**
 * Checks if the current environment is staging
 * @returns boolean indicating if running in staging
 */
export const isStaging = (): boolean => {
  if (typeof window === 'undefined') {
    // Server-side rendering - check API URL from environment variable
    const apiUrl = process.env.NEXT_PUBLIC_BEATLY_API_URL || '';
    return apiUrl.includes('staging.beatly.com');
  }
  return window.location.hostname === 'app.staging.beatly.com';
};

/**
 * Checks if the current environment is development
 * @returns boolean indicating if running in development
 */
export const isDevelopment = (): boolean => {
  if (typeof window === 'undefined') {
    // Server-side rendering - default to development if not production or staging
    return !isProduction() && !isStaging();
  }
  return (
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1' ||
    window.location.hostname.includes('localhost')
  );
};

/**
 * Gets the current environment name
 * @returns string representing the current environment
 */
export const getEnvironment = (): 'production' | 'staging' | 'development' => {
  if (isProduction()) return 'production';
  if (isStaging()) return 'staging';
  return 'development';
};

/**
 * Gets the appropriate domain suffix for external services based on environment
 * @param serviceName - The name of the external service (e.g., 'analytics', 'dashboard')
 * @returns The full URL for the service in the current environment
 */
export const getExternalServiceUrl = (serviceName: string): string => {
  const environment = getEnvironment();

  if (environment === 'staging') {
    return `https://${serviceName}.staging.beatly.com`;
  }
  return `https://${serviceName}.beatly.com`;
};

/**
 * Gets the API URL for the current environment
 * @returns The API URL based on the current environment
 */
export const getApiUrl = (): string => {
  return process.env.NEXT_PUBLIC_BEATLY_API_URL || '';
};

/**
 * Checks if the current environment is production or staging (not development)
 * @returns boolean indicating if running in a deployed environment
 */
export const isDeployed = (): boolean => {
  return isProduction() || isStaging();
};
